"use client";

import React, { useState, useEffect } from "react";
import { ArrowUpRight, Code, Cpu, Microscope, Sparkles } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const DEFAULT_MASCOT_SRC = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/c2386fca-8209-4748-ae37-47d275ce8104.png";
const pillars = [{
  id: "robotics",
  title: {
    vi: "Kỹ thuật Robotics",
    en: "Robotics engineering"
  },
  icon: Cpu,
  tag: {
    vi: "Phần cứng & Động học",
    en: "Hardware & Kinematics"
  },
  colorClass: "text-primary",
  accentBg: "bg-primary/15",
  borderHover: "hover:border-primary",
  description: {
    vi: "Học viên lắp ráp các vi điều khiển dạng module, bộ truyền động chính xác và khung gầm đa khớp nối, biến các ý tưởng cơ khí trực quan thành hệ thống chuyển động hoàn chỉnh.",
    en: "Learners assemble modular micro-controllers, precision actuators, and articulated chassis, translating tactile mechanical ideas into functional moving systems."
  }
}, {
  id: "stem",
  title: {
    vi: "Nghiên cứu STEM ứng dụng",
    en: "Applied STEM inquiry"
  },
  icon: Microscope,
  tag: {
    vi: "Phân tích & Vật lý",
    en: "Analysis & Physics"
  },
  colorClass: "text-accent",
  accentBg: "bg-accent/15",
  borderHover: "hover:border-accent",
  description: {
    vi: "Mô hình vật lý trở thành bàn thử nghiệm khoa học, nơi học sinh đo lực mô-men xoắn, hiệu chuẩn cảm biến quang học và kiểm chứng các định luật vật lý trong điều kiện ma sát thực tế.",
    en: "Physical assemblies become laboratory testbeds where children measure torque, calibrate optical sensors, and test scientific hypotheses against real-world friction."
  }
}, {
  id: "coding",
  title: {
    vi: "Lập trình thuật toán",
    en: "Algorithmic coding"
  },
  icon: Code,
  tag: {
    vi: "Logic & Phần mềm",
    en: "Logic & Software"
  },
  colorClass: "text-secondary",
  accentBg: "bg-secondary/15",
  borderHover: "hover:border-secondary",
  description: {
    vi: "Các thuật toán toán học trừu tượng mang lại kết quả tức thì khi lập trình viên nhí thiết lập quy trình tự vận hành, bộ lắng nghe sự kiện và vòng phản hồi trực tiếp trên robot.",
    en: "Abstract mathematical algorithms gain immediate consequence as young coders program autonomous routines, event listeners, and feedback loops directly onto their builds."
  }
}];
const labHighlights = [{
  id: "benches",
  label: {
    vi: "Bàn chế tác & lắp ráp kỹ thuật",
    en: "Fabrication & build benches"
  },
  detail: {
    vi: "Bàn gỗ phong khoảng thoáng rộng tích hợp lớp chống tĩnh điện ESD và khay linh kiện truy cập nhanh.",
    en: "High-clearance maple worktables equipped with ESD protection and quick-access component bins."
  }
}, {
  id: "monitors",
  label: {
    vi: "Trạm lập trình màn hình kép",
    en: "Dual-monitor code rigs"
  },
  detail: {
    vi: "Thiết bị chuyên dụng hỗ trợ lập trình IDE song song với quan sát dữ liệu cảm biến telemetry và mô phỏng thực tế.",
    en: "Dedicated developer terminals for side-by-side IDE scripting, sensor telemetry, and simulation."
  }
}, {
  id: "tracks",
  label: {
    vi: "Sàn thử nghiệm tự hành tiêu chuẩn",
    en: "Autonomous test tracks"
  },
  detail: {
    vi: "Mặt sàn đấu trường được hiệu chuẩn chuẩn xác cho bài toán vượt chướng ngại vật, bám line PID và thi đấu đối kháng.",
    en: "Calibrated arena floors for obstacle clearance, PID line tracking, and team challenge runs."
  }
}];
const textContent = {
  eyebrow: {
    vi: "Kiến trúc học viện // Lộ trình học tập liên tục",
    en: "Academy Architecture // Continuous Learning Path"
  },
  mainHeading: {
    vi: "Một lộ trình kết nối từ cơ chế vật lý đến mã nguồn thuần túy",
    en: "One connected path from physical mechanism to pure code"
  },
  mainParagraph: {
    vi: "ROBOTO thay thế các buổi workshop cuối tuần rời rạc bằng một lộ trình kỹ thuật thống nhất. Các mô hình phần cứng tạo trải nghiệm trực quan, thí nghiệm khoa học rèn luyện tư duy chặt chẽ, và phần mềm thuật toán trao cho mỗi cơ chế khả năng tự chủ ra quyết định.",
    en: "ROBOTO replaces fragmented weekend workshops with a unified engineering continuum. Hardware builds provide tangible relevance, scientific tests establish rigorous thinking, and algorithmic software gives every mechanism autonomous decision-making power."
  },
  facilityBadge: {
    vi: "Cơ sở vật chất chuyên biệt",
    en: "Purpose-Built Facilities"
  },
  facilityHeading: {
    vi: "Thiết kế chuẩn mực cho các kỹ sư nhí thực thụ",
    en: "Engineered for serious young builders"
  },
  facilityParagraph: {
    vi: "Không gian học tập của chúng tôi mô phỏng các phòng lab ươm mầm phần cứng hiện đại thay vì phòng học lý thuyết thụ động. Mỗi phòng trang bị bàn chế tác trực quan, đường thử nghiệm chuyên biệt và chuỗi công cụ phần mềm chuyên nghiệp giúp học viên thử nghiệm, gỡ lỗi và cải tiến không giới hạn.",
    en: "Our learning spaces replicate modern hardware incubation labs rather than passive lecture halls. Every room provides tactile prototyping surfaces, dedicated testing tracks, and professional software toolchains so students prototype, debug, and iterate without compromise."
  },
  imageHubBadge: {
    vi: "Trụ sở học viện chính // Phòng Lab A",
    en: "Main Academy Hub // Lab Unit A"
  },
  imageRatioBadge: {
    vi: "Tỷ lệ 1 Mentor : 8 Kỹ sư nhí",
    en: "1:8 Mentor-to-Engineer Ratio"
  },
  btnExploreLabs: {
    vi: "Khám phá các phòng lab",
    en: "Explore our academy labs"
  },
  btnViewPaths: {
    vi: "Xem lộ trình học tập",
    en: "View learning paths"
  }
};
export default function Ecosystem() {
  const [lang, setLang] = useState<"vi" | "en">(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("roboto_lang");
      return saved === "en" ? "en" : "vi";
    }
    return "vi";
  });
  useEffect(() => {
    const handleLangChange = (e: any) => {
      const newLang = e?.detail || localStorage.getItem("roboto_lang") || "vi";
      setLang(newLang === "en" ? "en" : "vi");
    };
    const handleStorage = () => {
      const saved = localStorage.getItem("roboto_lang");
      setLang(saved === "en" ? "en" : "vi");
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);
  const shouldReduceMotion = useReducedMotion();
  const easeCurve = [0.16, 1, 0.3, 1] as const;
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: easeCurve
      }
    }
  };
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: easeCurve
      }
    }
  };
  return <section id="ecosystemandlearningspaces" data-nav="dark" className="relative bg-foreground text-background py-20 md:py-28 overflow-hidden border-b border-background/15">
      {/* Precision Blueprint Grid Overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
      backgroundSize: "32px 32px"
    }} aria-hidden="true" />

      {/* Blueprint Spec Rail Line Accents */}
      <div className="pointer-events-none absolute top-0 left-8 md:left-16 bottom-0 w-px bg-background/10 hidden lg:block" />
      <div className="pointer-events-none absolute top-0 right-8 md:right-16 bottom-0 w-px bg-background/10 hidden lg:block" />

      {/* Static Floating Decorative Mascot Visual */}
      <div className="pointer-events-none select-none absolute top-6 right-4 sm:top-8 sm:right-8 md:right-12 lg:right-20 z-0 sm:z-10 opacity-70 sm:opacity-85 md:opacity-95" aria-hidden="true">
        <div className="size-20 sm:size-24 md:size-28 lg:size-32">
          <img src={DEFAULT_MASCOT_SRC} data-wvc-srcset={`${DEFAULT_MASCOT_SRC}-wvc-srcset`} data-wvc-sizes={`${DEFAULT_MASCOT_SRC}-wvc-sizes`} alt="" className="w-full h-full object-contain bg-transparent pointer-events-none select-none drop-shadow-md" />
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-24 relative z-10">
        {/* PART 1: THE ECOSYSTEM BLOCK */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={sectionVariants} className="mb-20 md:mb-28">
          {/* Eyebrow & Headline Header */}
          <div className="max-w-3xl mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md border border-background/15 bg-background/5 backdrop-blur-xs mb-4">
              <span className="size-2 rounded-full bg-secondary animate-pulse" />
              <span className="text-xs font-mono uppercase tracking-wider text-background/70">
                {textContent.eyebrow[lang]}
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-background tracking-tight leading-tight">
              {textContent.mainHeading[lang]}
            </h2>
            <p className="mt-4 text-base sm:text-lg text-background/80 leading-relaxed font-sans">
              {textContent.mainParagraph[lang]}
            </p>
          </div>

          {/* Connected Pillar Cards Grid */}
          <div className="relative">
            {/* Horizontal Connector Line in secondary accent (visible on md+ screens) */}
            <div className="hidden md:block absolute top-1/2 left-10 right-10 h-px bg-secondary/70 -translate-y-8 z-0 pointer-events-none" aria-hidden="true" />

            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.15
          }} className="grid grid-cols-1 md:grid-cols-3 gap-6 relative z-10">
              {pillars.map((pillar, i) => {
              const IconComponent = pillar.icon;
              return <motion.div key={pillar.id} data-index={i} variants={itemVariants} whileHover={shouldReduceMotion ? {} : {
                y: -4
              }} className="h-full">
                    <Card className={`h-full bg-background/5 text-background border border-background/15 backdrop-blur-xs transition-[border-color,box-shadow,background-color] duration-200 shadow-md hover:bg-background/10 ${pillar.borderHover} flex flex-col justify-between`}>
                      <CardHeader className="pb-4">
                        <div className="flex items-center justify-between gap-4 mb-3">
                          <div className={`size-12 rounded-lg ${pillar.accentBg} flex items-center justify-center border border-background/15`}>
                            <IconComponent className={`size-6 ${pillar.colorClass}`} />
                          </div>
                          <Badge variant="outline" className="text-2xs font-mono tracking-tight border-background/20 bg-background/10 text-background/90">
                            {pillar.tag[lang]}
                          </Badge>
                        </div>
                        <CardTitle className="text-xl font-serif font-semibold text-background">
                          {pillar.title[lang]}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="text-sm leading-relaxed text-background/75 font-sans">
                          {pillar.description[lang]}
                        </CardDescription>
                      </CardContent>
                    </Card>
                  </motion.div>;
            })}
            </motion.div>
          </div>
        </motion.div>

        {/* PART 2: LEARNING SPACES BLOCK */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={sectionVariants} className="pt-6 border-t border-background/15">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Laboratory Photograph Frame */}
            <div className="lg:col-span-7 order-2 lg:order-1">
              <div className="relative group">
                {/* Precision blueprint corner notches */}
                <div className="absolute -top-1.5 -left-1.5 size-3 border-t-2 border-l-2 border-primary z-20 pointer-events-none" />
                <div className="absolute -top-1.5 -right-1.5 size-3 border-t-2 border-r-2 border-secondary z-20 pointer-events-none" />
                <div className="absolute -bottom-1.5 -left-1.5 size-3 border-b-2 border-l-2 border-accent z-20 pointer-events-none" />
                <div className="absolute -bottom-1.5 -right-1.5 size-3 border-b-2 border-r-2 border-primary z-20 pointer-events-none" />

                {/* Main Image Container */}
                <div className="relative rounded-xl overflow-hidden border border-background/20 shadow-lg bg-background/5">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_laboratory_workbench_testing_zone.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_laboratory_workbench_testing_zone.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_laboratory_workbench_testing_zone.png-wvc-sizes" alt={lang === "vi" ? "Phòng lab robotics ROBOTO với khu vực thử nghiệm và bàn kỹ sư màn hình kép" : "ROBOTO robotics laboratory testing zones and dual-monitor student workbenches"} loading="lazy" decoding="async" className="w-full h-[320px] sm:h-[400px] lg:h-[440px] object-cover transition-transform duration-500 group-hover:scale-[1.01]" />

                  {/* Cleanroom Lab Metric Tag */}
                  <div className="absolute bottom-4 left-4 right-4 bg-foreground/90 backdrop-blur-md border border-background/20 p-3.5 rounded-lg flex items-center justify-between text-xs text-background">
                    <div className="flex items-center gap-2">
                      <span className="size-2 rounded-full bg-secondary" />
                      <span className="font-mono text-background font-medium">
                        {textContent.imageHubBadge[lang]}
                      </span>
                    </div>
                    <span className="text-background/70 hidden sm:inline font-mono">
                      {textContent.imageRatioBadge[lang]}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Laboratory Specifications Copy */}
            <div className="lg:col-span-5 order-1 lg:order-2 flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md border border-background/15 bg-background/5 backdrop-blur-xs mb-4 w-fit">
                <Sparkles className="size-3.5 text-accent" />
                <span className="text-xs font-mono uppercase tracking-wider text-background/70">
                  {textContent.facilityBadge[lang]}
                </span>
              </div>

              <h3 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-background tracking-tight leading-tight mb-4">
                {textContent.facilityHeading[lang]}
              </h3>

              <p className="text-base text-background/80 leading-relaxed mb-6 font-sans">
                {textContent.facilityParagraph[lang]}
              </p>

              {/* Lab Specification Points */}
              <div className="space-y-4 mb-8">
                {labHighlights.map((highlight, index) => <div key={highlight.id} data-index={index} className="p-3.5 rounded-lg border border-background/15 bg-background/5 flex flex-col gap-1 transition-colors hover:bg-background/10 hover:border-secondary/60">
                    <span className="text-sm font-semibold font-serif text-background flex items-center gap-2">
                      <span className="size-1.5 rounded-full bg-secondary" />
                      {highlight.label[lang]}
                    </span>
                    <span className="text-xs text-background/70 leading-normal pl-3.5 font-sans">
                      {highlight.detail[lang]}
                    </span>
                  </div>)}
              </div>

              {/* Navigation Action */}
              <div className="flex flex-wrap items-center gap-3">
                <Button asChild size="default" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
                  <Link to="/FacilitiesCentersPage">
                    <span>{textContent.btnExploreLabs[lang]}</span>
                    <ArrowUpRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="default" className="border-background/30 text-background bg-transparent hover:bg-background/20 hover:text-background">
                  <Link to="/ProgramsPage">{textContent.btnViewPaths[lang]}</Link>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}